import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Implicit.css';
import AuthModal from './AuthModal';
import Notification from './Notification';
import { useNavigate } from 'react-router-dom';

function Implicit() {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [leftImages, setLeftImages] = useState([]);
  const [rightImages, setRightImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [, setUser] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '' });
  const [timeRemaining, setTimeRemaining] = useState(60);
  const [pendingSessionEnd, setPendingSessionEnd] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  // Touch tracking
  const touchStartRef = useRef({ x: 0, y: 0 });

  // Two random prompt words
  const PROMPT_WORDS = [
    'drift', 'echo', 'bloom', 'rust', 'hollow', 'tender', 'fracture', 'silk',
    'ember', 'vapor', 'thorn', 'dust', 'ripple', 'ghost', 'pulse', 'shadow',
    'murmur', 'stone', 'glass', 'honey', 'orbit', 'moss', 'static', 'bone',
    'velvet', 'ash', 'tide', 'fog', 'spark', 'loom', 'grain', 'haze',
    'wander', 'fold', 'sway', 'blush', 'glow', 'trace', 'melt', 'nest'
  ];
  const [prompts] = useState(() => {
    const first = Math.floor(Math.random() * PROMPT_WORDS.length);
    let second = Math.floor(Math.random() * (PROMPT_WORDS.length - 1));
    if (second >= first) second++;
    return [PROMPT_WORDS[first], PROMPT_WORDS[second]];
  });
  const [showPrompt, setShowPrompt] = useState(true);

  // Fetch images
  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/cached-images?count=48');
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const data = await response.json();
      setImages(prev => [...prev, ...data]);
      setError(null);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchImages();
  }, []);

  // Check auth on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setIsAuthenticated(true);
        setUser(userData);
        const verifyToken = async () => {
          try {
            const response = await fetch('/api/users/me', {
              headers: { 'x-auth-token': token }
            });
            if (!response.ok) {
              handleLogout();
            } else {
              const freshUserData = await response.json();
              setUser(freshUserData);
            }
          } catch (error) {
            console.error('Error verifying token:', error);
          }
        };
        verifyToken();
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        handleLogout();
      }
    }
  }, []);

  // Flash prompt words then hide
  useEffect(() => {
    const id = setTimeout(() => setShowPrompt(false), 1000);
    return () => clearTimeout(id);
  }, []);

  // Session countdown timer
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Save session and navigate
  const saveSessionAndNavigate = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const allImages = [
        ...leftImages.map(img => ({ ...img, prompt: prompts[0] })),
        ...rightImages.map(img => ({ ...img, prompt: prompts[1] })),
      ];
      await Promise.all(allImages.map(item =>
        fetch('/api/saved-images', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
          body: JSON.stringify({
            archiveId: item.id,
            title: item.name,
            description: item.description,
            url: item.url,
            thumbnailUrl: item.thumbnailUrl,
            fullSizeUrl: item.fullSizeUrl || null,
          }),
        }).catch(() => {})
      ));
      const sessionTitle = `${prompts[0]} / ${prompts[1]}`;
      const response = await fetch('/api/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
        body: JSON.stringify({ images: allImages, title: sessionTitle }),
      });
      const data = await response.json();
      if (response.ok) {
        navigate(`/s/${data.code}`);
      } else {
        navigate('/session-review', { state: { images: allImages } });
      }
    } catch (error) {
      console.error('Error saving session:', error);
      const allImages = [
        ...leftImages.map(img => ({ ...img, prompt: prompts[0] })),
        ...rightImages.map(img => ({ ...img, prompt: prompts[1] })),
      ];
      navigate('/session-review', { state: { images: allImages } });
    }
  }, [leftImages, rightImages, prompts, navigate]);

  // End session
  const endSession = useCallback(async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    const totalImages = leftImages.length + rightImages.length;
    if (totalImages > 0) {
      if (isAuthenticated) {
        await saveSessionAndNavigate();
      } else {
        setPendingSessionEnd(true);
        setShowModal(true);
      }
    } else {
      setTimeRemaining(60);
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  }, [leftImages, rightImages, isAuthenticated, saveSessionAndNavigate]);

  // When timer reaches 0
  useEffect(() => {
    if (timeRemaining === 0) {
      endSession();
    }
  }, [timeRemaining, endSession]);

  // Advance to next image
  const advanceImage = useCallback(() => {
    setCurrentIndex(prev => {
      const next = prev + 1;
      if (next >= images.length - 5) {
        fetchImages();
      }
      return next;
    });
  }, [images.length]);

  // Choice handler — left assigns to left prompt, right assigns to right prompt
  const handleChoice = useCallback((direction) => {
    if (currentIndex >= images.length) return;

    setSwipeDirection(direction);

    setTimeout(() => {
      const item = images[currentIndex];
      if (direction === 'left') {
        setLeftImages(prev => {
          const exists = prev.find(img => img.id === item.id);
          if (!exists) return [...prev, item];
          return prev;
        });
      } else {
        setRightImages(prev => {
          const exists = prev.find(img => img.id === item.id);
          if (!exists) return [...prev, item];
          return prev;
        });
      }
      setNotification({ show: true, message: `Assigned to "${direction === 'left' ? prompts[0] : prompts[1]}"` });
      setTimeout(() => setNotification({ show: false, message: '' }), 2000);
      setSwipeDirection(null);
      advanceImage();
    }, 300);
  }, [currentIndex, images, advanceImage, prompts]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handleChoice('left');
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleChoice('right');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleChoice]);

  // Touch/swipe gestures
  useEffect(() => {
    const handleTouchStart = (e) => {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    };
    const handleTouchEnd = (e) => {
      const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
      const distance = Math.abs(dx);
      if (distance > 50) {
        handleChoice(dx > 0 ? 'right' : 'left');
      }
    };
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleChoice]);

  const handleAuthModalClose = (success, userData) => {
    setShowModal(false);
    if (success && userData) {
      setIsAuthenticated(true);
      setUser(userData);
      if (pendingSessionEnd) {
        setPendingSessionEnd(false);
        saveSessionAndNavigate();
      }
    } else {
      setPendingSessionEnd(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  if (loading && images.length === 0) {
    return <div className="loading">Loading...</div>;
  }

  if (error && images.length === 0) {
    return <div className="error">Error: {error}</div>;
  }

  const currentImage = images[currentIndex];

  return (
    <div className="implicit-container">
      {notification.show && <Notification message={notification.message} />}

      {/* Prompt words flash at start */}
      {showPrompt && (
        <>
          <div className="implicit-prompt implicit-prompt-left">{prompts[0]}</div>
          <div className="implicit-prompt implicit-prompt-right">{prompts[1]}</div>
        </>
      )}

      {/* Persistent prompt labels on edges */}
      {!showPrompt && (
        <>
          <div className="implicit-label implicit-label-left">
            {prompts[0]}
            <span className="implicit-label-count">{leftImages.length}</span>
          </div>
          <div className="implicit-label implicit-label-right">
            {prompts[1]}
            <span className="implicit-label-count">{rightImages.length}</span>
          </div>
        </>
      )}

      {/* Main card area */}
      <div className="implicit-stage">
        {currentImage && (
          <div className={`tinder-card ${swipeDirection ? `swipe-${swipeDirection}` : ''}`}>
            <img
              src={currentImage.thumbnailUrl}
              alt={currentImage.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://archive.org/images/notfound.png";
              }}
            />
          </div>
        )}
        {!currentImage && !loading && (
          <div className="tinder-empty">No more images</div>
        )}
      </div>

      {/* Left/Right click zones */}
      <div className="tinder-zone tinder-zone-left" onClick={() => handleChoice('left')} />
      <div className="tinder-zone tinder-zone-right" onClick={() => handleChoice('right')} />

      {/* Auth Modal */}
      <AuthModal isOpen={showModal} onClose={handleAuthModalClose} />

      {/* Timer line */}
      <div className="session-timer-line" style={{ width: `${(timeRemaining / 60) * 100}%` }} />

      {/* Done button */}
      <button className="session-done-button" onClick={endSession}>
        Done
      </button>
    </div>
  );
}

export default Implicit;
